import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericData, getStatusCount, StatusKey } from 'components/StatusSummary/helper';
import { Tooltip } from '@shape-ds/components';

export type StatusKeyMapItem = { [key: string]: number | string };

type StatusBoxProps<T> = {
  statusKey: string;
  statusKeyMap?: { [key: string]: StatusKeyMapItem };
  onChange?: (selectedValue: string) => void;
  data: T[];
  page?: string;
  activeItems?: string[];
  shouldShowCompletedActionStatus?: boolean;
  hideTitle?: boolean;
  shouldBlockEmptyFilter?: boolean;
};

const classificationMap = {
  NOT_USEFUL: 'NOT_USEFUL',
  USEFUL: 'USEFUL',
};

export const statusMap = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
};
const actionStatusMap = {
  INVESTIGATING: 'INVESTIGATING',
  ACTION_ISSUED: 'ACTION_ISSUED',
};

export const defaultStatusKeyMap: { [key: string]: StatusKeyMapItem } = {
  classification: classificationMap,
  status: statusMap,
  action_status: actionStatusMap,
};

const StatusBox = <T extends GenericData>({
  statusKey,
  data,
  onChange,
  page,
  activeItems,
  shouldShowCompletedActionStatus,
  statusKeyMap = defaultStatusKeyMap,
  hideTitle,
  shouldBlockEmptyFilter,
}: StatusBoxProps<T>) => {
  const keyList = statusKeyMap[statusKey] ? Object.keys(statusKeyMap[statusKey]) : [];

  const statusCounts = useMemo(() => {
    if (shouldShowCompletedActionStatus) {
      return getStatusCount(statusKey, data, [...keyList, StatusKey.Completed] as StatusKey[]);
    }
    return getStatusCount(statusKey, data, keyList as StatusKey[]);
  }, [data]);

  const { t } = useTranslation();
  const statusNamesList = Object.keys(statusCounts);

  if (statusNamesList.length === 0) {
    return null;
  }

  return (
    <div className={`status-box status-box-${statusKey}`}>
      {!hideTitle && (
        <span className="box-title">
          {t(`${page}:status_count_panel.status.${statusKey}.title`)}
        </span>
      )}
      <div className="box-items">
        {statusNamesList.map((item) => {
          return (
            <Tooltip
              title={t(`${page}:status_count_panel.empty_data`)}
              key={item}
              visible={Boolean(statusCounts[item] === 0 && shouldBlockEmptyFilter)}
            >
              <div
                className={`box-item ${item} ${activeItems?.includes(item) && 'box-item-active'}`}
                key={item}
                onClick={() => {
                  if (!(shouldBlockEmptyFilter && statusCounts[item] === 0)) {
                    onChange && onChange(item);
                  }
                }}
              >
                <span className="box-value">{statusCounts[item]}</span>
                <span className="box-caption">
                  {t(`${page}:status_count_panel.status.${statusKey}.${item}`)}
                </span>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default StatusBox;
